import type { VoidFunctionComponent } from 'react';

import { hasFix } from '../../../../../shared/vulns/remediation';
import DetailsPanel from '../../../generic/DetailsPanel';
import RemediationPanels from '../RemediationPanels';
import type { UpgradeDistance } from '../../../../../shared/vulns/types';

import DrawerSection from './Section';

type Props = {
  version: string;
  isLoading?: boolean;
  completeFix: string;
  completeFixDistance?: UpgradeDistance;
  partialFix: string;
  partialFixDistance?: UpgradeDistance;
  isUserDefinedDependency?: boolean;
  isDetectedViaCpe?: boolean;
  onUpdateDependency?: () => void;
};

/**
 * VulnerabilityDrawerRemediation shows information related to
 * the "fixability" of the currently selected vulnerability.
 */
const VulnerabilityDrawerRemediation: VoidFunctionComponent<Props> = ({
  version,
  isLoading,
  completeFix,
  completeFixDistance,
  partialFix,
  partialFixDistance,
  isUserDefinedDependency,
  isDetectedViaCpe,
  onUpdateDependency,
}) => {
  const hasCompleteFix = hasFix(completeFix);
  const hasPartialFix = hasFix(partialFix);

  // always show the complete fix if one is available
  const shouldShowCompleteFix = hasCompleteFix;
  // only show the partial fix if one is available and either:
  // 1. there is no complete fix
  // 2. the complete fix is different version than the partial fix
  const shouldShowPartialFix = hasPartialFix && (!hasCompleteFix || completeFix !== partialFix);
  // only show "no fix" if the vuln has neither a complete fix nor a partial fix
  const shouldShowNoFix = !(hasCompleteFix || hasPartialFix);

  return (
    <DrawerSection>
      <DetailsPanel id="package-inventory-vuln-drawer-remediation" target="Remediation" isLoading={isLoading}>
        <RemediationPanels
          version={version}
          isLoading={isLoading}
          shouldShowPartialFix={shouldShowPartialFix}
          shouldShowCompleteFix={shouldShowCompleteFix}
          shouldShowNoFix={shouldShowNoFix}
          completeFix={completeFix}
          completeFixDistance={completeFixDistance}
          partialFix={partialFix}
          partialFixDistance={partialFixDistance}
          shouldShowUnableToCalculate={isUserDefinedDependency}
          isDetectedViaCpe={isDetectedViaCpe}
          onUpdateDependency={onUpdateDependency}
        />
      </DetailsPanel>
    </DrawerSection>
  );
};

export default VulnerabilityDrawerRemediation;
